import React from 'react';
import { Box, Button } from '@mui/material';
import BackspaceIcon from '@mui/icons-material/Backspace';

/**
 * Mini numeric keypad fixed at the bottom of the screen.
 * Props:
 * - open: boolean
 * - onInput: (digit: string) => void
 * - onBackspace: () => void
 * - onClear: () => void
 * - onDone: () => void
 * - allowDecimal: when true, adds a . key and moves Done to its own row (for money entry)
 */
const NumericKeypad = ({ open, onInput, onBackspace, onClear, onDone, allowDecimal = false }) => {
  if (!open) return null;

  const rows = [
    ['1','2','3'],
    ['4','5','6'],
    ['7','8','9']
  ];

  return (
    <Box
      role="dialog"
      aria-label="Numeric keypad"
      sx={{
        position: 'fixed',
        left: 0,
        right: 0,
        bottom: 0,
        zIndex: (theme) => theme.zIndex.modal + 20,
        display: 'flex',
        justifyContent: 'center',
        // Transparent outer area; inner panel will carry the background
        bgcolor: 'transparent',
        color: 'black',
        py: 2,
        px: 2,
      }}
    >
      <Box
        sx={{
          width: '30vw',
          minWidth: 260,
          maxWidth: 420,
          display: 'flex',
          flexDirection: 'column',
          gap: 1.25,
          // Panel background fits keys only
          bgcolor: '#2b2b2b',
          p: 1.5,
          borderRadius: 2,
          boxShadow: '0 -8px 24px rgba(0,0,0,0.35)'
        }}
      >
        {rows.map((r, idx) => (
          <Box key={`row-${idx}`} sx={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: 1.25 }}>
            {r.map((d) => (
              <Button
                key={d}
                variant="contained"
                onClick={() => onInput && onInput(d)}
                sx={{
                  minWidth: 0,
                  height: 56,
                  borderRadius: 1,
                  bgcolor: '#f5f5f7',
                  color: 'black',
                  boxShadow: 'inset 0 0 0 1px rgba(0,0,0,0.12)',
                  '&:hover': { bgcolor: '#eeeeef' },
                  fontSize: '1.35rem',
                  fontWeight: 800,
                }}
              >
                {d}
              </Button>
            ))}
          </Box>
        ))}

        {allowDecimal ? (
          <>
            <Box sx={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: 1.25 }}>
              <Button
                variant="contained"
                onClick={() => onInput && onInput('.')}
                sx={{
                  minWidth: 0,
                  height: 56,
                  borderRadius: 1,
                  bgcolor: '#f5f5f7',
                  color: 'black',
                  boxShadow: 'inset 0 0 0 1px rgba(0,0,0,0.12)',
                  '&:hover': { bgcolor: '#eeeeef' },
                  fontSize: '1.35rem',
                  fontWeight: 800,
                }}
              >
                .
              </Button>
              <Button
                variant="contained"
                onClick={() => onInput && onInput('0')}
                sx={{
                  minWidth: 0,
                  height: 56,
                  borderRadius: 1,
                  bgcolor: '#f5f5f7',
                  color: 'black',
                  boxShadow: 'inset 0 0 0 1px rgba(0,0,0,0.12)',
                  '&:hover': { bgcolor: '#eeeeef' },
                  fontSize: '1.35rem',
                  fontWeight: 800,
                }}
              >
                0
              </Button>
              <Button
                variant="contained"
                onClick={() => onBackspace && onBackspace()}
                sx={{
                  minWidth: 0,
                  height: 56,
                  borderRadius: 1,
                  bgcolor: '#f5f5f7',
                  color: 'black',
                  boxShadow: 'inset 0 0 0 1px rgba(0,0,0,0.12)',
                  '&:hover': { bgcolor: '#eeeeef' },
                }}
              >
                <BackspaceIcon />
              </Button>
            </Box>
            <Button
              variant="contained"
              fullWidth
              onClick={() => onDone && onDone()}
              sx={{
                minWidth: 0,
                height: 56,
                borderRadius: 1,
                bgcolor: '#f5f5f7',
                color: '#1976d2',
                boxShadow: 'inset 0 0 0 1px rgba(0,0,0,0.12)',
                '&:hover': { bgcolor: '#eeeeef' },
                fontWeight: 800,
                fontSize: '1.15rem'
              }}
            >
              Done
            </Button>
          </>
        ) : (
        <Box sx={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: 1.25 }}>
          <Button
            variant="contained"
            onClick={() => onBackspace && onBackspace()}
            sx={{
              minWidth: 0,
              height: 56,
              borderRadius: 1,
              bgcolor: '#f5f5f7',
              color: 'black',
              boxShadow: 'inset 0 0 0 1px rgba(0,0,0,0.12)',
              '&:hover': { bgcolor: '#eeeeef' },
            }}
          >
            <BackspaceIcon />
          </Button>
          <Button
            variant="contained"
            onClick={() => onInput && onInput('0')}
            sx={{
              minWidth: 0,
              height: 56,
              borderRadius: 1,
              bgcolor: '#f5f5f7',
              color: 'black',
              boxShadow: 'inset 0 0 0 1px rgba(0,0,0,0.12)',
              '&:hover': { bgcolor: '#eeeeef' },
              fontSize: '1.35rem',
              fontWeight: 800,
            }}
          >
            0
          </Button>
          <Button
            variant="contained"
            onClick={() => onDone && onDone()}
            sx={{
              minWidth: 0,
              height: 56,
              borderRadius: 1,
              bgcolor: '#f5f5f7',
              color: '#1976d2',
              boxShadow: 'inset 0 0 0 1px rgba(0,0,0,0.12)',
              '&:hover': { bgcolor: '#eeeeef' },
              fontWeight: 800,
              fontSize: '1.15rem'
            }}
          >
            Done
          </Button>
        </Box>
        )}
      </Box>
    </Box>
  );
};

export default NumericKeypad;
